import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Gift, Download, Envelope, Plus, Trash, ArrowLeft, CheckCircle, Warning, ArrowRight } from '@phosphor-icons/react';
import { API_URL } from '../config/api';
import { SEO } from '../components/SEO';
import { BreadcrumbJsonLd } from '../components/BreadcrumbJsonLd';
import { RelatedTools } from '../components/RelatedTools';

const BLANK_ITEM = { device_key: 'rachio_3_8zone', purchase_cost_usd: 170, install_cost_usd: 0, purchase_date: new Date().toISOString().slice(0, 10) };

const RebateHelperPage = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [utility, setUtility] = useState(null); // null | 'round_rock' | 'crossroads' | 'mud' | 'unsure'
  const [form, setForm] = useState({
    applicant_name: '',
    service_address: '',
    water_account_number: '',
    applicant_email: '',
    applicant_phone: '',
    vendor_name: 'Amazon.com',
  });
  const [items, setItems] = useState([{ ...BLANK_ITEM }]);
  const [result, setResult] = useState(null);
  const [computing, setComputing] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/rebate/devices`)
      .then((r) => setDevices(r.data.devices))
      .catch(() => toast.error('Could not load device catalog'));
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (idx, k, v) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((arr) => [...arr, { ...BLANK_ITEM, device_key: 'rachio_wireless_rain_sensor', purchase_cost_usd: 40 }]);
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const buildPayload = () => ({
    program: 'round_rock',
    ...form,
    water_account_number: form.water_account_number || null,
    applicant_phone: form.applicant_phone || null,
    items: items.map((it) => ({
      device_key: it.device_key,
      purchase_cost_usd: parseFloat(it.purchase_cost_usd) || 0,
      install_cost_usd: parseFloat(it.install_cost_usd) || 0,
      purchase_date: it.purchase_date || null,
    })),
  });

  const validate = () => {
    if (!form.applicant_name || !form.service_address || !form.applicant_email) {
      toast.error('Name, service address, and email are required.');
      return false;
    }
    for (const it of items) {
      if (!it.device_key || !(parseFloat(it.purchase_cost_usd) > 0)) {
        toast.error('Every line item needs a device and a cost > $0.');
        return false;
      }
    }
    return true;
  };

  const onCompute = async () => {
    if (!validate()) return;
    setComputing(true);
    try {
      const r = await axios.post(`${API_URL}/api/rebate/compute`, buildPayload());
      setResult(r.data);
      toast.success(`You'll get back $${r.data.totals.total_claim_usd.toFixed(2)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not compute rebate.');
    } finally {
      setComputing(false);
    }
  };

  const onDownloadPdf = async () => {
    if (!validate()) return;
    try {
      const r = await axios.post(`${API_URL}/api/rebate/pdf`, buildPayload(), { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `rebate-application-${form.applicant_name.replace(/\s+/g, '-').toLowerCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('PDF generation failed.');
    }
  };

  const onEmailDraft = () => {
    if (!result) { toast.error('Compute first.'); return; }
    const { to, subject, body } = result.email_draft;
    window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="rebate-helper-page">
      <SEO title="Round Rock Rebate Helper — TurfPulse" description="Auto-fill your Round Rock smart irrigation rebate application in 90 seconds." />

      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="rebate-back-btn">
            <ArrowLeft size={14} /> Back
          </button>
          <span className="text-sm font-bold">Round Rock Rebate Helper</span>
          <div className="w-8" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 text-xs font-medium">
            <Gift size={12} weight="fill" /> City of Round Rock
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Stop leaving rebate money on the table.</h1>
          <p className="text-base text-muted-foreground">
            Enter your purchase details once. We generate the itemized receipt, the application PDF, and the email draft — ready to send to <code className="text-xs bg-muted/50 px-1 rounded">jwoods@roundrocktexas.gov</code>.
          </p>
        </div>

        {/* Eligibility gate — verify actual water utility before filling the form */}
        {!utility && (
          <Card className="border-2 border-amber-500/40 bg-amber-500/5" data-testid="rebate-eligibility-gate">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start gap-3">
                <Warning size={22} weight="fill" className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <h2 className="text-lg font-bold">Quick check: who actually bills you for water?</h2>
                  <p className="text-xs text-muted-foreground mt-1">
                    The rebate is only for <strong>direct City of Round Rock Water customers</strong>. Living in a Round Rock ZIP doesn't automatically mean you qualify — many neighborhoods are on MUD water (Crossroads, Brushy Creek MUD, etc.). Takes 10 seconds to confirm.
                  </p>
                </div>
              </div>
              <div className="space-y-2" data-testid="rebate-utility-options">
                <button type="button" onClick={() => setUtility('round_rock')} className="w-full text-left border border-emerald-500/30 hover:border-emerald-500/60 bg-background/60 rounded-md p-3 transition-colors group" data-testid="rebate-utility-round_rock">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold">City of Round Rock Water Utilities</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">Bill header says "City of Round Rock" · qualifies ✅</div>
                    </div>
                    <ArrowRight size={14} className="shrink-0 text-muted-foreground group-hover:text-emerald-600" />
                  </div>
                </button>
                <button type="button" onClick={() => setUtility('crossroads')} className="w-full text-left border border-border hover:border-amber-500/50 bg-background/60 rounded-md p-3 transition-colors" data-testid="rebate-utility-crossroads">
                  <div className="text-sm font-semibold">Crossroads Utility Services</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">Does not participate in conservation rebate programs (confirmed Feb 2026)</div>
                </button>
                <button type="button" onClick={() => setUtility('mud')} className="w-full text-left border border-border hover:border-amber-500/50 bg-background/60 rounded-md p-3 transition-colors" data-testid="rebate-utility-mud">
                  <div className="text-sm font-semibold">Brushy Creek MUD · Jonah SUD · Manville WSC · Other MUD</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">Most Central TX MUDs / WSCs don't offer this rebate — check directly</div>
                </button>
                <button type="button" onClick={() => setUtility('unsure')} className="w-full text-left border border-border hover:border-sky-500/50 bg-background/60 rounded-md p-3 transition-colors" data-testid="rebate-utility-unsure">
                  <div className="text-sm font-semibold">Not sure — I'll check my bill</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">Top-left of your water bill shows the utility name</div>
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Non-qualifier gate */}
        {utility && utility !== 'round_rock' && (
          <Card className="border-2 border-amber-500/40 bg-amber-500/5" data-testid="rebate-non-qualifier">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start gap-3">
                <Warning size={22} weight="fill" className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <h2 className="text-lg font-bold">
                    {utility === 'crossroads' && "Crossroads doesn't offer a rebate — but don't stop here."}
                    {utility === 'mud' && "Your MUD likely doesn't participate — worth a quick check anyway."}
                    {utility === 'unsure' && "Let's figure out your utility first."}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-1">
                    {utility === 'crossroads' && "Crossroads Utility Services confirmed (Feb 2026) they don't run any conservation rebate programs for their customers. The Round Rock city rebate only applies to direct city water accounts."}
                    {utility === 'mud' && "Most Central Texas MUDs (Municipal Utility Districts) and WSCs don't offer smart irrigation rebates, but a handful do. Call your billing entity directly to confirm before you assume."}
                    {utility === 'unsure' && "Grab your latest water bill. The utility name is in the top-left header. Or call Round Rock Water at (512) 218-5500 with your address — they'll tell you on the spot."}
                  </p>
                </div>
              </div>

              {utility !== 'unsure' && (
                <div className="bg-background/60 border border-amber-500/20 rounded-md p-3 text-xs space-y-2">
                  <div className="font-semibold text-sm">Still smart to upgrade — here's why:</div>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                    <li><strong>Business write-off</strong> — if the controller is used for any LLC / side-hustle (property mgmt, landscaping, content), it's a deductible equipment expense. A $170 Rachio ≈ $40–50 tax savings at typical SMB brackets.</li>
                    <li><strong>Water savings</strong> — Central TX smart controllers typically save $120–$250/yr on water by eliminating overwatering. 12–18 month payback regardless of rebate.</li>
                    <li><strong>Manufacturer sales</strong> — Rachio + Hunter run 15–25% off around Memorial Day and July 4th. Stack with Amazon Warehouse deals.</li>
                    <li><strong>Your HOA</strong> — some Round Rock HOAs fund resident conservation upgrades. Check before you file with the city.</li>
                  </ul>
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setUtility(null)} data-testid="rebate-change-utility">
                  ← Pick different utility
                </Button>
                {utility !== 'unsure' && (
                  <Button variant="outline" size="sm" onClick={() => setUtility('round_rock')} data-testid="rebate-override-utility">
                    I verified — I am on city water
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Qualifier confirmation banner */}
        {utility === 'round_rock' && (
          <div className="flex items-center gap-2 text-xs border border-emerald-500/40 bg-emerald-500/5 rounded-md p-2.5" data-testid="rebate-qualifier-banner">
            <CheckCircle size={14} weight="fill" className="text-emerald-600 dark:text-emerald-400 shrink-0" />
            <span>Verified: <strong>City of Round Rock Water Utilities</strong> — you qualify. Let's get your rebate filed.</span>
            <button onClick={() => setUtility(null)} className="ml-auto text-muted-foreground underline text-[11px]" data-testid="rebate-change-utility-2">change</button>
          </div>
        )}

        {/* Applicant */}
        {utility === 'round_rock' && (<>
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Who's applying</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Full name" required>
              <Input value={form.applicant_name} onChange={(e) => setField('applicant_name', e.target.value)} data-testid="rebate-name-input" />
            </Field>
            <Field label="Email" required>
              <Input type="email" value={form.applicant_email} onChange={(e) => setField('applicant_email', e.target.value)} data-testid="rebate-email-input" />
            </Field>
            <Field label="Service address" required className="sm:col-span-2">
              <Input value={form.service_address} onChange={(e) => setField('service_address', e.target.value)} placeholder="123 Main St, Round Rock TX 78664" data-testid="rebate-address-input" />
            </Field>
            <Field label="Water account #">
              <Input value={form.water_account_number} onChange={(e) => setField('water_account_number', e.target.value)} data-testid="rebate-account-input" />
            </Field>
            <Field label="Phone">
              <Input value={form.applicant_phone} onChange={(e) => setField('applicant_phone', e.target.value)} data-testid="rebate-phone-input" />
            </Field>
            <Field label="Vendor" className="sm:col-span-2">
              <Input value={form.vendor_name} onChange={(e) => setField('vendor_name', e.target.value)} data-testid="rebate-vendor-input" />
            </Field>
          </CardContent>
        </Card>

        {/* Items */}
        <Card>
          <CardHeader className="pb-3 flex-row items-center justify-between">
            <CardTitle className="text-base">Equipment purchased</CardTitle>
            <Button variant="outline" size="sm" onClick={addItem} data-testid="rebate-add-item-btn">
              <Plus size={14} className="mr-1" /> Add item
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {items.map((it, idx) => {
              const dev = devices.find((d) => d.key === it.device_key);
              return (
                <div key={idx} className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_1fr_auto] gap-2 items-end border border-border/50 rounded-md p-3" data-testid={`rebate-item-${idx}`}>
                  <Field label="Device" className="min-w-0">
                    <Select value={it.device_key} onValueChange={(v) => setItem(idx, 'device_key', v)}>
                      <SelectTrigger data-testid={`rebate-item-device-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {devices.map((d) => (
                          <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Cost ($)">
                    <Input type="number" inputMode="decimal" value={it.purchase_cost_usd} onChange={(e) => setItem(idx, 'purchase_cost_usd', e.target.value)} data-testid={`rebate-item-cost-${idx}`} />
                  </Field>
                  <Field label="Install ($)">
                    <Input type="number" inputMode="decimal" value={it.install_cost_usd} onChange={(e) => setItem(idx, 'install_cost_usd', e.target.value)} data-testid={`rebate-item-install-${idx}`} />
                  </Field>
                  <Field label="Purchase date">
                    <Input type="date" value={it.purchase_date} onChange={(e) => setItem(idx, 'purchase_date', e.target.value)} data-testid={`rebate-item-date-${idx}`} />
                  </Field>
                  <Button variant="ghost" size="icon" onClick={() => removeItem(idx)} disabled={items.length === 1} data-testid={`rebate-item-remove-${idx}`}>
                    <Trash size={16} />
                  </Button>
                  {dev && !dev.watersense && dev.category === 'controller' && (
                    <div className="sm:col-span-5 text-[10px] text-red-600 dark:text-red-400">⚠ Must be WaterSense to qualify.</div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button size="lg" className="flex-1" onClick={onCompute} disabled={computing} data-testid="rebate-compute-btn">
            {computing ? 'Calculating…' : 'Calculate my rebate'}
          </Button>
          <Button size="lg" variant="outline" onClick={onDownloadPdf} data-testid="rebate-pdf-btn">
            <Download size={16} className="mr-1.5" /> Download PDF
          </Button>
          <Button size="lg" variant="outline" onClick={onEmailDraft} disabled={!result} data-testid="rebate-email-btn">
            <Envelope size={16} className="mr-1.5" /> Open email draft
          </Button>
        </div>

        {/* Result */}
        {result && (
          <Card className="border-2 border-emerald-500/40 bg-emerald-500/5" data-testid="rebate-result">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-start gap-3">
                <CheckCircle size={28} weight="fill" className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                <div>
                  <div className="text-3xl font-bold font-mono">${result.totals.total_claim_usd.toFixed(2)}</div>
                  <div className="text-sm text-muted-foreground">
                    Your rebate {result.totals.capped_by_program && <span className="text-amber-600">(hit the ${result.totals.annual_cap_usd} program cap)</span>}
                    {result.earliest_submission_deadline && <> · submit by <strong>{result.earliest_submission_deadline}</strong></>}
                  </div>
                </div>
              </div>

              <div className="space-y-1.5 text-xs">
                {result.line_items.map((li, i) => (
                  <div key={i} className="flex items-center justify-between border-b border-emerald-500/10 pb-1.5" data-testid={`rebate-result-line-${i}`}>
                    <span>{li.device_label}</span>
                    <span className="font-mono font-semibold">${li.eligible_rebate_usd.toFixed(2)} <span className="text-muted-foreground font-normal">({li.rebate_note})</span></span>
                  </div>
                ))}
              </div>

              <div className="bg-background/60 rounded-md border border-emerald-500/20 p-3 space-y-1 text-xs">
                <div className="font-semibold text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 mb-1">Itemized receipt (paste-ready)</div>
                <pre className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed" data-testid="rebate-receipt-preview">{result.receipt_description}</pre>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Help */}
        <p className="text-[11px] text-muted-foreground text-center pt-2">
          Not city-affiliated. Rebate rules sourced from <a className="underline" href="https://www.roundrocktexas.gov/wp-content/uploads/2025/09/Smart-Irrigation-Rebate-_Oct-2025.pdf" target="_blank" rel="noopener noreferrer">the official Round Rock PDF</a>. Verify before submitting.
        </p>
        </>)}
      </main>
      <div className="container mx-auto px-4 sm:px-6 max-w-6xl pb-12">
        <RelatedTools currentPath="/rachio/rebate-helper" />
      </div>
      <BreadcrumbJsonLd items={[
        { label: 'Home', path: '/' },
        { label: 'Rachio', path: '/rachio' },
        { label: 'Round Rock Rebate Helper', path: '/rachio/rebate-helper' },
      ]} />
    </div>
  );
};

const Field = ({ label, children, className = '', required = false }) => (
  <div className={`space-y-1 ${className}`}>
    <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </Label>
    {children}
  </div>
);

export default RebateHelperPage;
